import { SectionHeader } from '../primitives/SectionHeader'
import { scoreBandCssVar } from '../primitives/score'
import type { ScoreBucket } from '../selectors'

export interface ScoreDistributionChartProps {
  data: ScoreBucket[]
}

export function ScoreDistributionChart({ data }: ScoreDistributionChartProps) {
  const hasAny = data.some(b => b.count > 0)
  const max = Math.max(1, ...data.map(b => b.count))

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
      <SectionHeader
        title="Score distribution"
        subLabel="Investigation score across top entities per type (max 10 / type)"
        guide={{
          to: '/docs/investigation-analysis#investigation-score',
          hint: 'Buckets of width 10 over 0–100. Bands match the page legend: high ≥70, notable ≥40, low ≥15.',
        }}
      />
      {!hasAny ? (
        <div className="rounded-lg border border-dashed border-[var(--border)] p-6 flex items-center justify-center">
          <p className="text-xs text-[var(--muted-foreground)]">No scored entities yet</p>
        </div>
      ) : (
        <>
          <div className="relative h-40">
            {/* Band background rails */}
            <div className="absolute inset-0 flex pointer-events-none">
              <div className="flex-[15] border-r border-dashed border-[var(--border)]/60" />
              <div
                className="flex-[25]"
                style={{
                  background: 'color-mix(in oklab, var(--chart-1) 6%, transparent)',
                }}
              />
              <div
                className="flex-[30]"
                style={{
                  background: 'color-mix(in oklab, var(--chart-4) 6%, transparent)',
                }}
              />
              <div
                className="flex-[30]"
                style={{
                  background: 'color-mix(in oklab, var(--destructive) 6%, transparent)',
                }}
              />
            </div>
            {/* Bars */}
            <div className="absolute inset-0 flex items-end gap-1 px-1">
              {data.map(h => {
                const pct = (h.count / max) * 100
                return (
                  <div
                    key={h.bucket}
                    className="flex-1 flex flex-col items-center justify-end relative group"
                    title={`${h.bucket}: ${h.count}`}
                  >
                    <div
                      className="w-full rounded-t-sm"
                      style={{
                        height: `${pct}%`,
                        background: scoreBandCssVar(h.band),
                        opacity: h.count === 0 ? 0.15 : 0.9,
                      }}
                    />
                    <span className="absolute -top-4 text-4xs tabular-nums text-[var(--muted-foreground)]">
                      {h.count || ''}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
          {/* X axis */}
          <div className="flex gap-1 px-1 mt-1">
            {data.map(h => (
              <div
                key={h.bucket}
                className="flex-1 text-center text-4xs text-[var(--muted-foreground)] tabular-nums"
              >
                {h.lower}
              </div>
            ))}
          </div>
        </>
      )}
      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 pt-3 border-t border-[var(--border)]">
        <span className="inline-flex items-center gap-1.5 text-3xs text-[var(--muted-foreground)]">
          <span className="inline-block size-2 rounded-sm bg-[var(--destructive)]" />
          70+ high
        </span>
        <span className="inline-flex items-center gap-1.5 text-3xs text-[var(--muted-foreground)]">
          <span className="inline-block size-2 rounded-sm bg-[var(--chart-4)]" />
          40+ notable
        </span>
        <span className="inline-flex items-center gap-1.5 text-3xs text-[var(--muted-foreground)]">
          <span className="inline-block size-2 rounded-sm bg-[var(--chart-1)]" />
          15+ low
        </span>
      </div>

      {/* Screen-reader backup table */}
      <table className="sr-only">
        <caption>Score distribution across top entities per type</caption>
        <thead>
          <tr>
            <th>Bucket</th>
            <th>Count</th>
            <th>Band</th>
          </tr>
        </thead>
        <tbody>
          {data.map(b => (
            <tr key={b.bucket}>
              <td>{b.bucket}</td>
              <td>{b.count}</td>
              <td>{b.band}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
